import {
  ClientResponse,
  ConversationEventType,
  ConversationParticipant,
  SendFeedbackParams,
} from '@stripe-internal/conversations-client';
import {ConversationsApiSendMessageEndpointRequest} from '../generated/types';
import {ApiMethods} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type SendFeedbackHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
    options: {allowNoContent: true},
  ) => ReturnType<ApiMethods['sendMessage']>;
  emitConversationEvent: (event: ConversationEventType) => void;
  getParticipant: () => ConversationParticipant | undefined;
};

export class SendFeedbackHandler {
  private readonly deps: SendFeedbackHandlerDeps;

  constructor(deps: SendFeedbackHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: SendFeedbackParams): Promise<ClientResponse> => {
    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const sendStartedAt = Date.now();
    const response = await this.deps.sendMessage(
      params.conversationId,
      SendFeedbackHandler.buildPayload({params, participant}),
      {allowNoContent: true},
    );
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    if (!response.data) {
      return {code: 200};
    }

    const {newMessages} = FrameworkAdapter.diffConversation({
      previousMessageIds: [],
      apiConversation: response.data,
      requestContext: {},
      user: participant,
      minimumTimestamp: sendStartedAt,
    });
    newMessages.forEach((message) => {
      this.deps.emitConversationEvent({
        name: 'conversation-message-added',
        conversationId: params.conversationId,
        participantId: message.participant.id,
        messageId: message.id,
        nonce: message.nonce,
        message,
      });
    });

    return {code: 200};
  };

  private static buildPayload({
    params,
    participant,
  }: {
    params: SendFeedbackParams;
    participant: ConversationParticipant;
  }): ConversationsApiSendMessageEndpointRequest {
    return {
      id: ApiMethods.generateMessageId(),
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts: [
        {
          id: 1,
          type: 'thumbs_feedback',
          content: {
            text: params.feedback,
            feedback_value: params.feedback,
            message_id: params.responseId,
          },
        },
      ],
    };
  }
}
